import { useEffect, useState } from "react";
import {
    Alert, Card, Grid, Icon, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDPeriodo from "components/MDPeriodo";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { get, postFile } from "utils/WebService";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import PrestacaoContasUmc from "./PrestacaoContasUmc";

// Colunas da planilha que mudam de um pagamento para outro, na ordem do arquivo. As fixas do layout (CNPJ do cliente,
// nº da PC, tipo de pagamento, comissões, datas de importação e permanência) vão só na planilha.
const COLUNAS = [
    { campo: "n_boleto", rotulo: "N. do Boleto" },
    { campo: "processo", rotulo: "Processo" },
    { campo: "cpf_cnpj", rotulo: "CNPJ/CPF" },
    { campo: "devedor", rotulo: "Devedor", minWidth: 200 },
    { campo: "estado", rotulo: "Estado" },
    { campo: "especie", rotulo: "Espécie" },
    { campo: "titulo", rotulo: "Título" },
    { campo: "parcela", rotulo: "Parcela", numero: true },
    { campo: "vl_titulo", rotulo: "Vl. Título", moeda: true },
    { campo: "data_documento", rotulo: "Data Documento" },
    { campo: "vencimento", rotulo: "Vencimento" },
    { campo: "vl_pagamento", rotulo: "Vl. Pagamento", moeda: true },
    { campo: "juros", rotulo: "Juros", moeda: true },
    { campo: "multa", rotulo: "Multa", moeda: true },
    { campo: "vl_desconto", rotulo: "Vl. Desconto", moeda: true },
    { campo: "vl_credito", rotulo: "Vl. Crédito", moeda: true },
    { campo: "recebimento", rotulo: "Recebimento" },
    { campo: "pagamento", rotulo: "Pagamento" },
    { campo: "dias_atraso", rotulo: "Dias Atraso", numero: true },
    { campo: "historico", rotulo: "Histórico" },
];

const numeroPc = (n) => String(n ?? "").padStart(5, "0");

const escaparHtml = (texto) => String(texto ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export default function ExportarPrestacaoContas() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    const [dataInicial, setDataInicial] = useState("");
    const [dataFinal, setDataFinal] = useState("");
    const [cliente, setCliente] = useState(null);
    const [clientesList, setClientesList] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [gerando, setGerando] = useState(false);
    const [previa, setPrevia] = useState(null);
    const [gerada, setGerada] = useState(null);
    const [pagina, setPagina] = useState(0);
    const [porPagina, setPorPagina] = useState(50);
    // Cliente com o modelo da UMC (Ferramentas > Clientes): a PC é por data de pagamento, com o arquivo de retorno
    const [modeloUmc, setModeloUmc] = useState(false);

    useEffect(() => {
        get("/pessoa/list-all")
            .then(res => setClientesList(res.list || res || []))
            .catch(() => setClientesList([]));
    }, []);

    useEffect(() => {
        const id = cliente?.id || cliente?.cvId;
        setModeloUmc(false);
        if (!id) return;
        get(`/relatorios/prestacao-contas/umc/modelo?cliente=${encodeURIComponent(id)}`)
            .then(res => setModeloUmc(!!res?.umc))
            .catch(() => setModeloUmc(false));
    }, [cliente]);

    // Mudou o filtro: a prévia deixa de valer (o Gerar usa exatamente os filtros dos dados que estão na tela)
    useEffect(() => {
        setPrevia(null);
        setGerada(null);
    }, [cliente, dataInicial, dataFinal]);

    const parametros = () =>
        `dataInicial=${dataInicial}&dataFinal=${dataFinal}&clienteId=${encodeURIComponent(cliente.id || cliente.cvId)}`;

    const verDados = () => {
        if (!cliente) {
            errorAlert("Selecione o cliente.");
            return;
        }
        if (!dataInicial || !dataFinal) {
            errorAlert("Selecione o período do pagamento.");
            return;
        }
        setBuscando(true);
        setGerada(null);
        get(`/relatorios/prestacao-contas/previa?${parametros()}`)
            .then(dados => {
                setPrevia(dados);
                setPagina(0);
            })
            .catch(err => errorAlert(err?.message || "Erro ao buscar os pagamentos da prestação de contas."))
            .finally(() => setBuscando(false));
    };

    const gerar = () => {
        const destino = previa.salvarEm
            ? `O arquivo será salvo em <b>${escaparHtml(previa.salvarEm)}</b> (não é baixado no computador).`
            : "A pasta da Prestação de Contas não está configurada: o arquivo só será baixado.";
        infoAviso(
            `Gerar a prestação de contas de <b>${escaparHtml(previa.cliente)}</b> com ${previa.quantidade} pagamento(s), `
            + `valor para repasse de <b>${toCurrency(previa.totalCredito)}</b>?<br/><br/>`
            + `A PC usa o próximo número da sequência (previsto: <b>${numeroPc(previa.proximoNumero)}</b>). ${destino}`,
            () => {
                setGerando(true);
                postFile(`/relatorios/prestacao-contas/gerar?${parametros()}&quantidade=${previa.quantidade}&total=${previa.totalCredito}`, {})
                    .then(res => {
                        const numero = res?.headers?.get("X-Pc-Numero");
                        // Salvo na pasta: o postFile não baixa (res.salvoEm)
                        const caminho = res?.salvoEm || null;
                        setGerada({ numero, caminho, nome: res?.nome });
                        successAlert(`PC nº <b>${numeroPc(numero)}</b> gerada`
                            + (caminho ? ` e salva em <b>${escaparHtml(caminho)}</b>.` : ". O arquivo foi baixado (pasta não configurada)."));
                    })
                    .catch(err => errorAlert(err?.message || "Erro ao gerar a prestação de contas."))
                    .finally(() => setGerando(false));
            }
        );
    };

    const celulaCab = { fontWeight: "bold", fontSize: "0.75rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
    const celula = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", py: 1 };

    const valor = (coluna, linha) => {
        const v = linha[coluna.campo];
        if (v === null || v === undefined || v === "") return "-";
        return coluna.moeda ? toCurrency(v) : v;
    };

    const linhas = previa?.linhas || [];
    const linhasDaPagina = linhas.slice(pagina * porPagina, pagina * porPagina + porPagina);

    const seletorCliente = (
        <Grid item xs={12} md={5}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
            <MDSelect
                isMulti={false}
                name="cliente"
                placeholder="Selecione um cliente..."
                value={cliente}
                options={clientesList}
                getOptionValue={(option) => option.id || option.cvId}
                getOptionLabel={(option) => option.nomeComum || option.nomeCompleto || option.nome || ""}
                onChange={(val) => setCliente(val)}
            />
        </Grid>
    );

    if (cliente && modeloUmc) {
        return <PrestacaoContasUmc cliente={cliente} seletorCliente={seletorCliente} />;
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Card sx={{ px: 2, py: 1.5 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        {seletorCliente}
                        <Grid item xs={12} sm={8} md={4}>
                            <MDPeriodo label="Período do pagamento" inicio={dataInicial} fim={dataFinal}
                                onChange={({ inicio, fim }) => { setDataInicial(inicio); setDataFinal(fim); }} />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
                            <MDButton variant="contained" color="info" size="medium" fullWidth sx={{ height: 38 }}
                                onClick={verDados} disabled={buscando || gerando}>
                                <Icon sx={{ mr: 1 }}>search</Icon>{buscando ? "Buscando..." : "Ver dados"}
                            </MDButton>
                        </Grid>
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="text" sx={{ px: 1 }}>
                                Confira os pagamentos antes de gerar: só o "Gerar" usa o número da sequência da PC.
                            </MDTypography>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            {previa && (
                <Grid item xs={12}>
                    <Card sx={{ p: 2 }}>
                        <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1.5}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block">
                                    {previa.cliente}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {previa.quantidade} pagamento(s) · Valor para repasse: <b>{toCurrency(previa.totalCredito)}</b>
                                    {" "}(soma do Vl. Crédito, o mesmo quadro da planilha)
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {gerada
                                        ? <>Gerada como PC nº <b>{numeroPc(gerada.numero)}</b>.</>
                                        : <>Nº da PC previsto: <b>{numeroPc(previa.proximoNumero)}</b> (o próximo da sequência, usado só ao gerar).</>}
                                </MDTypography>
                            </MDBox>
                            <MDButton variant="contained" color="info" size="medium"
                                onClick={gerar} disabled={gerando || !!gerada || previa.quantidade === 0}>
                                <Icon sx={{ mr: 1 }}>{gerada ? "check" : "description"}</Icon>
                                {gerando ? "Gerando..." : gerada ? "PC gerada" : `Gerar PC nº ${numeroPc(previa.proximoNumero)}`}
                            </MDButton>
                        </MDBox>

                        {gerada ? (
                            <Alert severity="success" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                {gerada.caminho
                                    ? <>Arquivo salvo em <b>{gerada.caminho}</b>.</>
                                    : <>Arquivo baixado{gerada.nome ? <> (<b>{gerada.nome}</b>)</> : null}. A pasta da Prestação de Contas não está configurada.</>}
                                {" "}Para gerar outra PC, clique em "Ver dados" de novo.
                            </Alert>
                        ) : previa.salvarEm ? (
                            <Alert severity="info" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                Ao gerar, o arquivo será salvo em <b>{previa.salvarEm}</b> (não é baixado no computador).
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                A pasta da Prestação de Contas não está configurada (Parametrização do Sistema &gt; Pastas e
                                Arquivos): ao gerar, o arquivo só será baixado.
                            </Alert>
                        )}

                        <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: 560 }}>
                            <Table size="small" stickyHeader sx={{ minWidth: 1800 }}>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        {COLUNAS.map(c => (
                                            <TableCell key={c.campo} sx={{ ...celulaCab, minWidth: c.minWidth }}
                                                align={c.moeda || c.numero ? "right" : "left"}>
                                                {c.rotulo}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {linhas.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={COLUNAS.length} sx={{ ...celula, py: 3 }}>
                                                Nenhum pagamento deste cliente no período.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {linhasDaPagina.map((linha, idx) => (
                                        <TableRow key={pagina * porPagina + idx} hover>
                                            {COLUNAS.map(c => (
                                                <TableCell key={c.campo} align={c.moeda || c.numero ? "right" : "left"}
                                                    sx={{ ...celula, whiteSpace: c.minWidth ? "normal" : "nowrap" }}>
                                                    {valor(c, linha)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {linhas.length > 0 && (
                            <TablePagination
                                component="div"
                                count={linhas.length}
                                page={pagina}
                                onPageChange={(e, nova) => setPagina(nova)}
                                rowsPerPage={porPagina}
                                onRowsPerPageChange={(e) => {
                                    setPorPagina(parseInt(e.target.value, 10));
                                    setPagina(0);
                                }}
                                labelRowsPerPage="Itens por pág:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                rowsPerPageOptions={[25, 50, 100, 500]}
                                sx={{
                                    color: cores.texto,
                                    ".MuiTablePagination-toolbar": { minHeight: 36, paddingLeft: 1, paddingRight: 1 },
                                    ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0, color: cores.texto },
                                    ".MuiTablePagination-select": { fontSize: "0.75rem", color: cores.texto },
                                    ".MuiTablePagination-selectIcon": { color: cores.texto },
                                    ".MuiTablePagination-actions button": { color: cores.texto },
                                }}
                            />
                        )}
                        <MDTypography variant="caption" color="text" display="block" mt={1}>
                            A planilha leva também as colunas fixas do layout: CNPJ do cliente, nº da PC, tipo de pagamento,
                            comissões e as datas de importação e de permanência.
                        </MDTypography>
                    </Card>
                </Grid>
            )}
        </Grid>
    );
}
